const AudioContextClass = () => window.AudioContext || window.webkitAudioContext

let audioContext = null
let noiseBuffer = null
let soundUnlockBound = false

export const DEFAULT_NOTIFICATION_SOUND = "prisme"
export const DEFAULT_REGISTRATION_SOUND = "arpege"

// Chaque son est une courte séquence de notes synthétisées (Web Audio), donc
// aucun fichier audio à héberger. Une note accepte :
//   freq / to (glissando), at (départ), dur, peak, type, detune
// et `noise: true` pour un bruit filtré (frappes, souffles).
export const NOTIFICATION_SOUNDS = {
  cloche_cristalline: [
    { freq: 880, at: 0, dur: 0.6, peak: 0.055 },
    { freq: 2428, at: 0, dur: 0.45, peak: 0.018 },
    { freq: 4752, at: 0, dur: 0.3, peak: 0.008 }
  ],
  accord_doux: [
    { freq: 523.25, at: 0, dur: 0.5, peak: 0.035, attack: 0.08 },
    { freq: 659.25, at: 0.02, dur: 0.5, peak: 0.03, attack: 0.08 },
    { freq: 783.99, at: 0.04, dur: 0.5, peak: 0.028, attack: 0.08 }
  ],
  arpege: [
    { freq: 587.33, at: 0, dur: 0.14, peak: 0.045, type: "triangle" },
    { freq: 739.99, at: 0.07, dur: 0.14, peak: 0.045, type: "triangle" },
    { freq: 880, at: 0.14, dur: 0.16, peak: 0.045, type: "triangle" },
    { freq: 1174.66, at: 0.21, dur: 0.3, peak: 0.05, type: "triangle" }
  ],
  melodie: [
    { freq: 659.25, at: 0, dur: 0.12, peak: 0.05, type: "triangle" },
    { freq: 987.77, at: 0.1, dur: 0.12, peak: 0.05, type: "triangle" },
    { freq: 880, at: 0.2, dur: 0.12, peak: 0.045, type: "triangle" },
    { freq: 1318.51, at: 0.3, dur: 0.28, peak: 0.05, type: "triangle" }
  ],
  ta_da: [
    { freq: 523.25, at: 0, dur: 0.1, peak: 0.05, type: "triangle" },
    { freq: 659.25, at: 0.06, dur: 0.1, peak: 0.05, type: "triangle" },
    { freq: 783.99, at: 0.12, dur: 0.12, peak: 0.05, type: "triangle" },
    { freq: 1046.5, at: 0.2, dur: 0.34, peak: 0.06, type: "triangle" },
    { freq: 1318.51, at: 0.2, dur: 0.34, peak: 0.03, type: "sine" }
  ],
  cascade: [
    { freq: 1318.51, at: 0, dur: 0.45, peak: 0.04 },
    { freq: 1567.98, at: 0.05, dur: 0.42, peak: 0.03 },
    { freq: 1975.53, at: 0.1, dur: 0.4, peak: 0.025 },
    { freq: 2637.02, at: 0.15, dur: 0.35, peak: 0.018 },
    { freq: 3135.96, at: 0.2, dur: 0.28, peak: 0.012 }
  ],
  carillon_eglise: [
    { freq: 392, at: 0, dur: 0.55, peak: 0.05, attack: 0.02 },
    { freq: 784, at: 0, dur: 0.4, peak: 0.02 },
    { freq: 523.25, at: 0.22, dur: 0.55, peak: 0.045, attack: 0.02 },
    { freq: 1046.5, at: 0.22, dur: 0.4, peak: 0.016 }
  ],
  sonnette: [
    { freq: 587.33, at: 0, dur: 0.32, peak: 0.055 },
    { freq: 1760, at: 0, dur: 0.22, peak: 0.018 },
    { freq: 440, at: 0.28, dur: 0.42, peak: 0.05 },
    { freq: 1320, at: 0.28, dur: 0.28, peak: 0.014 }
  ],
  pluck: [
    { noise: true, at: 0, dur: 0.02, peak: 0.05, filter: "highpass", freq: 2000 },
    { freq: 587.33, at: 0, dur: 0.22, peak: 0.06, type: "triangle" },
    { freq: 1174.66, at: 0, dur: 0.1, peak: 0.02, type: "sine" }
  ],
  marimba_roll: [
    { freq: 329.63, at: 0, dur: 0.16, peak: 0.05, type: "triangle" },
    { freq: 392, at: 0.08, dur: 0.16, peak: 0.048, type: "triangle" },
    { freq: 493.88, at: 0.16, dur: 0.18, peak: 0.046, type: "triangle" },
    { freq: 659.25, at: 0.24, dur: 0.32, peak: 0.05, type: "triangle" },
    { freq: 1318.51, at: 0.24, dur: 0.12, peak: 0.015, type: "sine" }
  ],
  toc_toc: [
    { noise: true, at: 0, dur: 0.05, peak: 0.09, filter: "lowpass", freq: 1200 },
    { freq: 160, to: 110, at: 0, dur: 0.09, peak: 0.08, type: "sine" },
    { noise: true, at: 0.14, dur: 0.05, peak: 0.08, filter: "lowpass", freq: 1100 },
    { freq: 150, to: 100, at: 0.14, dur: 0.09, peak: 0.07, type: "sine" }
  ],
  verre: [
    { noise: true, at: 0, dur: 0.03, peak: 0.04, filter: "highpass", freq: 2500 },
    { freq: 1480, at: 0, dur: 0.5, peak: 0.045 },
    { freq: 2960, at: 0, dur: 0.28, peak: 0.016 },
    { freq: 4440, at: 0, dur: 0.18, peak: 0.008 }
  ],
  plouf: [
    { freq: 900, to: 220, at: 0, dur: 0.18, peak: 0.07 },
    { noise: true, at: 0.02, dur: 0.12, peak: 0.02, filter: "lowpass", freq: 900 }
  ],
  bloup: [
    { freq: 320, to: 760, at: 0, dur: 0.12, peak: 0.08 },
    { freq: 760, to: 520, at: 0.11, dur: 0.14, peak: 0.05 }
  ],
  bulles_doubles: [
    { freq: 700, to: 1100, at: 0, dur: 0.09, peak: 0.05 },
    { freq: 900, to: 1500, at: 0.13, dur: 0.11, peak: 0.045 }
  ],
  boing: [
    { freq: 300, to: 620, at: 0, dur: 0.1, peak: 0.06, type: "triangle" },
    { freq: 620, to: 380, at: 0.09, dur: 0.1, peak: 0.045, type: "triangle" },
    { freq: 380, to: 520, at: 0.18, dur: 0.14, peak: 0.03, type: "triangle" }
  ],
  ressort: [
    { freq: 240, to: 720, at: 0, dur: 0.08, peak: 0.055, type: "triangle" },
    { freq: 720, to: 280, at: 0.07, dur: 0.08, peak: 0.045, type: "triangle" },
    { freq: 280, to: 640, at: 0.14, dur: 0.08, peak: 0.04, type: "triangle" },
    { freq: 640, to: 360, at: 0.21, dur: 0.12, peak: 0.03, type: "triangle" }
  ],
  yoink: [
    { freq: 420, to: 1250, at: 0, dur: 0.16, peak: 0.06, type: "triangle" },
    { freq: 840, to: 2500, at: 0, dur: 0.16, peak: 0.02, type: "sine" }
  ],
  souffle: [
    { noise: true, at: 0, dur: 0.3, peak: 0.045, filter: "bandpass", freq: 700, to: 2600, q: 3 }
  ],
  ondulation: [
    { freq: 440, to: 660, at: 0, dur: 0.18, peak: 0.04, attack: 0.05 },
    { freq: 660, to: 495, at: 0.16, dur: 0.2, peak: 0.035, attack: 0.04 },
    { freq: 495, to: 880, at: 0.34, dur: 0.28, peak: 0.03, attack: 0.05 }
  ],
  sonar: [
    { freq: 680, at: 0, dur: 0.18, peak: 0.05, attack: 0.04 },
    { freq: 680, at: 0.28, dur: 0.32, peak: 0.028, attack: 0.06 },
    { noise: true, at: 0.02, dur: 0.12, peak: 0.01, filter: "bandpass", freq: 680, q: 8 }
  ],
  cosmique: [
    { freq: 1568, to: 784, at: 0, dur: 0.26, peak: 0.04, type: "sine" },
    { freq: 2350, to: 1175, at: 0.04, dur: 0.26, peak: 0.02, type: "sine" },
    { noise: true, at: 0, dur: 0.24, peak: 0.012, filter: "bandpass", freq: 3000, to: 900, q: 4 }
  ],
  prisme: [
    { freq: 740, at: 0, dur: 0.35, peak: 0.035, attack: 0.04 },
    { freq: 932, at: 0.04, dur: 0.35, peak: 0.03, attack: 0.04 },
    { freq: 1174, at: 0.08, dur: 0.4, peak: 0.028, attack: 0.04 },
    { freq: 1480, at: 0.12, dur: 0.45, peak: 0.022, attack: 0.05 }
  ],
  trille: [
    { freq: 1046.5, at: 0, dur: 0.06, peak: 0.04, type: "triangle" },
    { freq: 1174.66, at: 0.06, dur: 0.06, peak: 0.04, type: "triangle" },
    { freq: 1046.5, at: 0.12, dur: 0.06, peak: 0.038, type: "triangle" },
    { freq: 1174.66, at: 0.18, dur: 0.06, peak: 0.036, type: "triangle" },
    { freq: 1318.51, at: 0.24, dur: 0.22, peak: 0.04, type: "triangle" }
  ],
  battement: [
    { noise: true, at: 0, dur: 0.06, peak: 0.08, filter: "lowpass", freq: 180 },
    { freq: 70, to: 45, at: 0, dur: 0.1, peak: 0.09 },
    { noise: true, at: 0.22, dur: 0.07, peak: 0.07, filter: "lowpass", freq: 160 },
    { freq: 62, to: 40, at: 0.22, dur: 0.12, peak: 0.08 }
  ],
  message: [
    { noise: true, at: 0, dur: 0.025, peak: 0.035, filter: "highpass", freq: 1800 },
    { freq: 784, to: 523.25, at: 0.02, dur: 0.12, peak: 0.05, type: "triangle" },
    { freq: 659.25, at: 0.16, dur: 0.22, peak: 0.045, type: "triangle" },
    { freq: 987.77, at: 0.16, dur: 0.18, peak: 0.02, type: "sine" }
  ]
}

export const notificationSoundEnabled = () => {
  const value = document.body?.dataset?.notificationSoundEnabled
  return value !== "0" && value !== "false"
}

export const selectedNotificationSound = () => {
  const key = document.body?.dataset?.notificationSound || DEFAULT_NOTIFICATION_SOUND
  return NOTIFICATION_SOUNDS[key] ? key : DEFAULT_NOTIFICATION_SOUND
}

export const selectedRegistrationSound = () => {
  const key = document.body?.dataset?.registrationSound || DEFAULT_REGISTRATION_SOUND
  return NOTIFICATION_SOUNDS[key] ? key : DEFAULT_REGISTRATION_SOUND
}

export const unlockNotificationSound = () => {
  const Ctx = AudioContextClass()
  if (!Ctx) return

  if (!audioContext) audioContext = new Ctx()
  if (audioContext.state === "suspended") audioContext.resume()
}

export const bindNotificationSoundUnlock = () => {
  if (soundUnlockBound) return
  soundUnlockBound = true
  document.addEventListener("pointerdown", unlockNotificationSound, { capture: true })
  document.addEventListener("keydown", unlockNotificationSound, { capture: true })
}

const shapeGain = (ctx, note, start) => {
  const gain = ctx.createGain()
  const attack = note.attack || 0.012
  const peak = note.peak || 0.05
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(peak, start + attack)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + note.dur)
  gain.connect(ctx.destination)
  return gain
}

const ensureNoiseBuffer = (ctx) => {
  if (noiseBuffer) return noiseBuffer

  const length = Math.floor(ctx.sampleRate * 0.5)
  noiseBuffer = ctx.createBuffer(1, length, ctx.sampleRate)
  const data = noiseBuffer.getChannelData(0)
  for (let i = 0; i < length; i += 1) {
    data[i] = Math.random() * 2 - 1
  }
  return noiseBuffer
}

const playNoise = (ctx, note, start) => {
  const source = ctx.createBufferSource()
  source.buffer = ensureNoiseBuffer(ctx)
  source.loop = true

  const filter = ctx.createBiquadFilter()
  filter.type = note.filter || "bandpass"
  filter.frequency.setValueAtTime(note.freq || 1000, start)
  if (note.to) filter.frequency.exponentialRampToValueAtTime(note.to, start + note.dur)
  if (note.q) filter.Q.setValueAtTime(note.q, start)

  source.connect(filter)
  filter.connect(shapeGain(ctx, note, start))
  source.start(start)
  source.stop(start + note.dur)
}

const playTone = (ctx, note, start) => {
  const oscillator = ctx.createOscillator()
  oscillator.type = note.type || "sine"
  oscillator.frequency.setValueAtTime(note.freq, start)
  if (note.to) oscillator.frequency.exponentialRampToValueAtTime(note.to, start + note.dur)
  if (note.detune) oscillator.detune.setValueAtTime(note.detune, start)

  oscillator.connect(shapeGain(ctx, note, start))
  oscillator.start(start)
  oscillator.stop(start + note.dur)
}

export const playNotificationSound = (key = selectedNotificationSound()) => {
  const Ctx = AudioContextClass()
  if (!Ctx) return

  if (!audioContext) audioContext = new Ctx()
  if (audioContext.state === "suspended") {
    audioContext.resume().then(() => {
      if (audioContext.state === "running") playNotificationSound(key)
    }).catch(() => {})
    return
  }

  const notes = NOTIFICATION_SOUNDS[key] || NOTIFICATION_SOUNDS[DEFAULT_NOTIFICATION_SOUND]
  const now = audioContext.currentTime
  notes.forEach((note) => {
    const start = now + (note.at || 0)
    if (note.noise) playNoise(audioContext, note, start)
    else playTone(audioContext, note, start)
  })
}

export const playIncomingNotificationSound = (kind = "change") => {
  if (!notificationSoundEnabled()) return
  const key = kind === "registration" ? selectedRegistrationSound() : selectedNotificationSound()
  playNotificationSound(key)
}
